import styled from '@emotion/styled';
import Link from 'next/link';
import { useRouter } from 'next/router';

import { screen } from '@/components/common/breakpoints';
import { ButtonLink } from '@/components/common/Button';
import { Image } from '@/components/common/image';
import { Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { transparentize } from '@/styles/utils';

export interface AuthorTagBlockProps {
  author: {
    name: string;
    slug: {
      current: string;
    };
    image?: any;
  };
  tags: Array<{ label: string; value: string }>;
  backgroundColour: string;
}

const AuthorTagBlock = ({
  author,
  tags,
  backgroundColour,
}: AuthorTagBlockProps) => {
  const { locale } = useRouter();

  const colour = `var(--colour-${backgroundColour}-tint)`;
  const getHref = (tag) => {
    const searchKey = tag.replace(/ /g, '+').replace(/&/g, '%26');
    return `/${locale}/blog?search=${searchKey}`;
  };

  return (
    <Section verticalPadding>
      <Container backgroundColour="--surface-primary-subtle">
        <LeftPanel>
          {author.image ? (
            <ImageContainer>
              <Image {...author.image} sizes="50vw" />
            </ImageContainer>
          ) : (
            <IconContainer>
              <Logo src="/images/icon.svg" alt="Multiverse logo" />
            </IconContainer>
          )}
        </LeftPanel>
        <RightPanel>
          <TopPanel>
            <ContentContainer>
              <Paragraph size="body-md">{author.name}</Paragraph>
              <Author>
                <div>
                  <span>Read </span>
                  <Link href={`/${locale}/blog/author/${author.slug.current}`}>
                    <b>more posts</b>
                  </Link>
                  <span>&nbsp;by this author </span>
                </div>
              </Author>
            </ContentContainer>
          </TopPanel>
          <Line colour={backgroundColour}>
            <hr />
          </Line>
          <ButtonPanel>
            {!!tags &&
              tags.map((tag) => (
                <ButtonContainer id={tag.value} key={tag.value}>
                  <ButtonLink
                    href={getHref(tag.value)}
                    backgroundOfParent={colour}
                  >
                    {tag.value}
                  </ButtonLink>
                </ButtonContainer>
              ))}
          </ButtonPanel>
        </RightPanel>
      </Container>
    </Section>
  );
};

const Container = styled.div<{ backgroundColour: string }>`
  overflow: hidden;
  display: flex;
  gap: var(--spacing-small);
  flex-direction: column;
  padding: var(--spacing-large);

  background-color: var(--background-warm-base);

  ${screen.md} {
    border-radius: var(--radius-m);
    padding: var(--spacing-500) var(--spacing-500) var(--spacing-small);

    flex-direction: row;
  }

  ${screen.xl} {
    max-width: var(--desktop-max-content-width);
  }
`;

const LeftPanel = styled.div``;

const RightPanel = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const Line = styled.div<{ colour: string }>`
  width: 90%;
  hr {
    background: ${transparentize('--border-warm-base', 30)};
    opacity: 0.3;
    height: 1px;
    border: none;
  }
`;
const TopPanel = styled.div`
  ${screen.md} {
    display: flex;
    flex-direction: row;
  }
`;
const ContentContainer = styled.div`
  p {
    font-size: 26px;
    font-weight: var(--font-weight-heavy);
  }
  ${screen.md} {
    width: 75%;
    padding-right: var(--spacing-1600);
  }
`;
const IconContainer = styled.div``;
const ImageContainer = styled.div`
  img {
    width: 44px;
  }
`;
const Author = styled.div`
  margin-top: var(--spacing-xx-small);
  display: flex;
  flex-direction: row;
  font-size: 20px;
  line-height: 32px;
  div:first-of-type {
    margin-bottom: var(--spacing-xx-small);
  }
  span {
    display: inline;
    font-size: 20px;
    line-height: 32px;
  }
  a {
    color: inherit;
  }
  a:hover {
    text-decoration: underline;
  }
  p {
    font-size: 26px;
    line-height: 38px;
  }
  ${screen.md} {
    flex-direction: row;
    span {
      display: inline;
    }
  }
`;
const Logo = styled.img`
  margin-bottom: var(--spacing-small);
  width: 44px;
  img {
    width: 44px;
  }
`;
const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding-top: var(--spacing-x-small);
  padding-bottom: var(--spacing-x-small);

  span {
    display: unset;
  }

  a {
    text-transform: uppercase;
    font-size: 16px;
    font-weight: var(--font-weight-heavy);
    letter-spacing: 1.36px;
    text-wrap: wrap;
    max-width: 100%;
  }

  ${screen.md} {
    padding-right: 30px;
  }
`;
const ButtonPanel = styled.div`
  margin-bottom: var(--spacing-large);
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--spacing-xxx-small) var(--spacing-small);

  ${screen.md} {
    margin-bottom: var(--spacing-small);
  }
`;
export { AuthorTagBlock };
